import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { QarepaBaseSubsDirective } from '@qarepa/common';
import { AccountSnackbarsService } from '@qaroni-app/account/services/account-snackbars.service';
import { AccountService } from '@qaroni-app/account/services/account.service';
import { UserDTO } from '@qaroni-app/account/types/account';
import { QaroniMatDialog } from '@qaroni-core/conf/dialog/qaroni-mat-dialog';
import { ImagesConfig } from '@qaroni-core/conf/images/images.config';
import { FilesValidations } from '@qaroni-core/conf/validators/file-validations';
import { ConfirmationDialogData } from '@qaroni-core/types/qaroni-confirmation-dialog-data';
import { QaroniDialogConfig } from '@qaroni-core/types/qaroni-dialog.config';
import { QaroniMatDialogConfigDataContentCenter } from '@qaroni-core/types/qaroni-mat-dialog-config-data-animate';
import { ConfirmationDialogComponent } from '@qaroni-shared/components/confirmation-dialog/confirmation-dialog.component';
import { FilesUploadService } from '@qaroni-shared/services/files-upload.service';
import { FileUpload } from '@qaroni-shared/types/file-upload';

@Component({
  selector: 'qaroni-account-avatar',
  standalone: true,
  imports: [MatIconModule, MatMenuModule],
  templateUrl: './account-avatar.component.html',
  styleUrl: './account-avatar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountAvatarComponent
  extends QarepaBaseSubsDirective
  implements OnInit
{
  @Input({ required: true }) id: number | null = 0;
  @Input({ required: true }) image: string | null = null;
  @Input({ required: true }) name: string = '';

  public ImagesConfig = ImagesConfig;
  public file: File | null = null;
  public isErrorFile = false;

  private dialog = inject(QaroniMatDialog);
  private accountService = inject(AccountService);
  private snackbar = inject(AccountSnackbarsService);
  private filesUploadService = inject(FilesUploadService);

  public tokenImage$ = this.filesUploadService.tokenImage$;

  ngOnInit(): void {
    this.subs.add(this.tokenImage$.subscribe(this.getToken));
  }

  private getToken = (fileUpload: FileUpload | null): void => {
    if (!(fileUpload && fileUpload.token)) {
      return;
    }

    const userDTO: UserDTO = {
      imageToken: fileUpload.token,
      name: this.name,
    };

    this.accountService.accountUpdate(userDTO);
  };

  public onDropFile(event: Event): void {
    const { files } = event.target as HTMLInputElement;
    const mb = 2097152;

    if (!files) {
      return;
    }

    if (!FilesValidations.fileSize(files[0].size, mb)) {
      this.snackbar.exceededSizeImage();
      return;
    }

    if (!FilesValidations.extension(files[0].name)) {
      this.snackbar.formatNotAllowed();
      return;
    }

    if (
      FilesValidations.fileSize(files[0].size, mb) &&
      FilesValidations.extension(files[0].name)
    ) {
      this.isErrorFile = false;
      let formData = new FormData();
      formData = FilesValidations.buildMultipartWithImage(
        formData,
        'file',
        files[0]
      );

      this.filesUploadService.upload(formData);
      this.file = files[0];
    } else {
      this.isErrorFile = true;
    }
  }

  public onDelete(): void {
    if (!this.id) {
      return;
    }

    if (!this.image) {
      return;
    }

    const dialogData: ConfirmationDialogData = {
      title: `Eliminar imagen`,
      message: `¿Estás seguro que deseas eliminar la imagen de perfil?. Esta acción no se puede deshacer.`,
      confirmIcon: `delete`,
      confirmText: `Sí, eliminar`,
    };

    const dialogConfig = new QaroniDialogConfig<ConfirmationDialogData>(
      QaroniMatDialogConfigDataContentCenter
    );

    dialogConfig.data = dialogData;

    const dialog = this.dialog.open<
      ConfirmationDialogComponent,
      ConfirmationDialogData,
      boolean
    >(ConfirmationDialogComponent, dialogConfig);

    this.subs.add(dialog.afterClosed().subscribe(this.getConfirmation));
  }

  private getConfirmation = (confirmation: boolean | undefined) => {
    if (!confirmation) {
      return;
    }

    if (!this.id) {
      return;
    }

    this.accountService.deleteImage(this.id);
  };
}
